import { useEffect, useState } from 'react';
import { Phone, MapPin, Menu, X, Instagram, Facebook } from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import { BUSINESS } from '@/data/business';

const NAV = [
    { id: 'boutique', label: 'Boutique', testid: 'landing-nav-boutique' },
    { id: 'sectors', label: 'Secteurs', testid: 'landing-nav-sectors' },
    { id: 'categories', label: 'Produits', testid: LANDING.navProducts },
    { id: 'brands', label: 'Marques', testid: LANDING.navBrands },
    { id: 'services', label: 'Services', testid: LANDING.navServices },
    { id: 'clients', label: 'Clients', testid: LANDING.navClients },
    { id: 'contact', label: 'Contact', testid: LANDING.navContact },
];

// Inline SVG for TikTok (lucide doesn't ship one)
const TikTokIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M19.5 6.7a6.6 6.6 0 01-3.9-1.2v9.6a5.7 5.7 0 11-5-5.6v3a2.7 2.7 0 102 2.6V2h3a3.6 3.6 0 00.6 2 3.6 3.6 0 003.3 1.8z" />
    </svg>
);

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const scrollTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <header
            data-testid={LANDING.header}
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled ? 'glass border-b border-white/10' : 'bg-transparent'
            }`}
        >
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
                <button
                    data-testid={LANDING.headerLogo}
                    onClick={() => scrollTo('hero')}
                    className="flex items-center gap-2.5 group shrink-0"
                    aria-label="MANU VET - retour en haut"
                >
                    <img
                        src={BUSINESS.logo}
                        alt="MANU VET — Spécialiste vêtements de travail Aulnay-sous-Bois"
                        className="h-5 sm:h-6 w-auto object-contain"
                    />
                </button>

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-0.5">
                    {NAV.map((item) => (
                        <button
                            key={item.id}
                            data-testid={item.testid}
                            onClick={() => scrollTo(item.id)}
                            className="px-3 py-2 text-xs font-semibold text-white/70 hover:text-[#FFD500] transition-colors uppercase tracking-wider"
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {/* Socials — desktop only */}
                    <div className="hidden md:flex items-center gap-1 mr-1">
                        <a
                            href={BUSINESS.instagramUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram MANU VET"
                            data-testid="landing-header-instagram"
                            className="h-9 w-9 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-[#FFD500] hover:border-[#FFD500] transition-colors"
                        >
                            <Instagram className="h-4 w-4" />
                        </a>
                        <a
                            href={BUSINESS.facebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook MANU VET"
                            data-testid="landing-header-facebook"
                            className="h-9 w-9 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-[#FFD500] hover:border-[#FFD500] transition-colors"
                        >
                            <Facebook className="h-4 w-4" />
                        </a>
                        <a
                            href={BUSINESS.tiktokUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="TikTok MANU VET"
                            data-testid="landing-header-tiktok"
                            className="h-9 w-9 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-[#FFD500] hover:border-[#FFD500] transition-colors"
                        >
                            <TikTokIcon className="h-4 w-4" />
                        </a>
                    </div>

                    <a
                        data-testid={LANDING.headerCallBtn}
                        href={`tel:${BUSINESS.phoneTel}`}
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-transparent border border-[#FFD500] text-[#FFD500] font-bold uppercase tracking-wider text-xs hover:bg-[#FFD500] hover:text-black transition-colors corner-cut"
                    >
                        <Phone className="h-4 w-4" />
                        <span>{BUSINESS.phone}</span>
                    </a>
                    <a
                        data-testid={LANDING.headerVisitBtn}
                        href={BUSINESS.googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 bg-[#FFD500] text-black font-bold uppercase tracking-wider text-xs hover:bg-white transition-colors corner-cut"
                    >
                        <MapPin className="h-4 w-4" />
                        <span>Visiter la boutique</span>
                    </a>
                    <button
                        data-testid={LANDING.mobileMenuToggle}
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden h-10 w-10 inline-flex items-center justify-center border border-white/15 text-white"
                        aria-label="Menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {/* Hazard stripe under header */}
            <div className="h-1 w-full hazard-stripes-thin opacity-90" />

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid={LANDING.mobileMenu}
                    className="lg:hidden glass border-t border-white/10"
                >
                    <nav className="px-5 py-4 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => scrollTo(item.id)}
                                className="text-left py-3 text-sm font-semibold uppercase tracking-wider text-white/80 hover:text-[#FFD500] border-b border-white/5"
                            >
                                {item.label}
                            </button>
                        ))}
                        <div className="mt-3 flex gap-2">
                            <a
                                href={`tel:${BUSINESS.phoneTel}`}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#FFD500] text-black font-bold uppercase tracking-wider text-xs"
                            >
                                <Phone className="h-4 w-4" /> {BUSINESS.phone}
                            </a>
                            <a
                                href={BUSINESS.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-12 w-12 inline-flex items-center justify-center border border-white/15 text-white/80"
                                aria-label="Instagram"
                            >
                                <Instagram className="h-5 w-5" />
                            </a>
                            <a
                                href={BUSINESS.facebookUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-12 w-12 inline-flex items-center justify-center border border-white/15 text-white/80"
                                aria-label="Facebook"
                            >
                                <Facebook className="h-5 w-5" />
                            </a>
                            <a
                                href={BUSINESS.tiktokUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-12 w-12 inline-flex items-center justify-center border border-white/15 text-white/80"
                                aria-label="TikTok"
                            >
                                <TikTokIcon className="h-5 w-5" />
                            </a>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
};
